import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Button, Space, Tag, message, Skeleton, Divider, InputNumber } from 'antd';
import { userService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

const UserCredits = () => {
  const { user, updateUserBalance, isReadOnly } = useAuth();

  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packages, setPackages] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plans, setPlans] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [bulkDesired, setBulkDesired] = useState(0);
  const [bulkQuote, setBulkQuote] = useState(null);
  const [bulkPricing, setBulkPricing] = useState([]);
  const [purchasingBulk, setPurchasingBulk] = useState(false);

  const balance = user?.balance || {};
  const totalCreditsAvailable = balance.total_credits_available || 0;
  const planCreditsAvailable = balance.plan_credits_available || 0;
  const bulkCredits = balance.bulk_credits || 0;
  const planName = balance.plan_name || 'Sin plan';
  const planDescription = balance.plan_description || '';
  const needsRenewal = balance.needs_renewal || false;
  const renewalDate = balance.plan_renewal_date || null;

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const data = await userService.getCreditPackages();
        setPackages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        message.error('No se pudieron cargar los paquetes de créditos');
      } finally {
        setLoadingPackages(false);
      }
    };

    const loadPlans = async () => {
      try {
        const data = await userService.getAvailablePlans();
        setPlans(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        // Si falla, solo dejar vacía la lista de planes
      } finally {
        setLoadingPlans(false);
      }
    };

    const loadBulkPricing = async () => {
      try {
        const data = await userService.getBulkPricing();
        setBulkPricing(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      }
    };

    loadPackages();
    loadPlans();
    loadBulkPricing();
  }, []);

  useEffect(() => {
    const quote = async () => {
      if (bulkDesired > 0) {
        try {
          const data = await userService.getBulkQuote(bulkDesired);
          setBulkQuote(data);
        } catch (e) {
          console.error(e);
          setBulkQuote(null);
        }
      } else {
        setBulkQuote(null);
      }
    };
    quote();
  }, [bulkDesired]);

  const refreshBalance = async () => {
    try {
      const data = await userService.getBalance();
      updateUserBalance(data);
    } catch (e) {
      console.error('Error refrescando balance', e);
    }
  };

  const handlePurchase = async (pkgId) => {
    setPurchasing(true);
    try {
      await userService.purchaseCreditsPackage(pkgId);
      message.success('Paquete comprado exitosamente');
      await refreshBalance();
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.message || 'Error al comprar paquete');
    } finally {
      setPurchasing(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(true);
    try {
      const email = user?.email;
      if (!email) {
        message.error('No se encontró tu email en el perfil.');
        return;
      }
      const resp = await userService.generatePaymentLink({ email, plan_id: planId });
      const url = resp?.payment_link;
      if (url) {
        message.success('Redirigiendo al proveedor de pago...');
        window.location.href = url; // o window.open(url, '_blank') si prefieres nueva pestaña
      } else {
        message.error('No se recibió el enlace de pago.');
      }
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.error || e?.response?.data?.message || 'Error al generar el enlace de pago');
    } finally {
      setUpgrading(false);
    }
  };

  const handlePurchaseBulk = async () => {
    if (!bulkDesired || bulkDesired <= 0) {
      message.warning('Ingrese una cantidad válida de créditos');
      return;
    }
    setPurchasingBulk(true);
    try {
      const email = user?.email;
      if (!email) {
        message.error('No se encontró tu email en el perfil.');
        return;
      }
      const resp = await userService.generatePaymentLink({ email, quantity: bulkDesired });
      const url = resp?.payment_link;
      if (url) {
        message.success('Redirigiendo al proveedor de pago...');
        window.location.href = url; // o window.open(url, '_blank')
      } else {
        message.error('No se recibió el enlace de pago.');
      }
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.error || e?.response?.data?.message || 'Error al generar el enlace de pago');
    } finally {
      setPurchasingBulk(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={4}>Tu balance actual</Title>
          <Space wrap>
            <Tag color="blue">Total: {totalCreditsAvailable}</Tag>
            <Tag color="green">Plan: {planCreditsAvailable}</Tag>
            <Tag color="magenta">A granel: {bulkCredits}</Tag>
          </Space>
          <Text strong>Plan actual: {planName}</Text>
          {planDescription && <Text type="secondary">{planDescription}</Text>}
          {renewalDate && (
            <Text type={needsRenewal ? 'danger' : 'secondary'}>
              {needsRenewal ? 'Plan vencido' : 'Renovación'}: {renewalDate}
            </Text>
          )}
        </Space>
      </Card>

      <Card title="Cambiar plan">
        {loadingPlans ? (
          <Skeleton active />
        ) : (
          <Row gutter={[16, 16]}>
            {plans.length === 0 ? (
              <Col span={24}>
                <Text>No hay planes disponibles.</Text>
              </Col>
            ) : (
              plans.map((plan) => (
                <Col xs={24} sm={12} md={8} lg={6} key={plan.id}>
                  <Card bordered>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Title level={5}>{plan.name || plan.title || 'Plan'}</Title>
                      {plan.description && <Text>{plan.description}</Text>}
                      <Space wrap>
                        {plan.credits && (
                          <Tag color="blue">{plan.credits} créditos por periodo</Tag>
                        )}
                        {plan.price && (
                          <Tag color="geekblue">
                            {(plan.currency || 'MXN')} ${plan.price}
                          </Tag>
                        )}
                      </Space>
                      {plan.name === planName && <Tag color="green">Plan actual</Tag>}
                      {!isReadOnly && <Button
                        type="primary"
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={upgrading || plan.name === planName}
                        loading={upgrading}
                      >
                        Cambiar a este plan
                      </Button>}
                    </Space>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        )}
      </Card>

      {!isReadOnly && (
      <Card title="Comprar créditos a granel">
        <>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text>Ingrese la cantidad de créditos a comprar</Text>
            <InputNumber
              min={1}
              value={bulkDesired}
              onChange={(val) => setBulkDesired(Number(val) || 0)}
              style={{ width: '100%', maxWidth: 200 }}
            />
            {bulkQuote ? (
              <Space wrap>
                <Tag color="geekblue">
                  Precio unitario: {bulkQuote.currency} ${bulkQuote.unit_price}
                </Tag>
                <Tag color="blue">
                  Total: {bulkQuote.currency} ${bulkQuote.total_price} por {bulkQuote.quantity} créditos
                </Tag>
              </Space>
            ) : (
              <Text type="secondary">Se mostrará la cotización aquí.</Text>
            )}

            <Button
              type="primary"
              onClick={handlePurchaseBulk}
              disabled={!bulkDesired || purchasingBulk}
              loading={purchasingBulk}
            >
              Comprar
            </Button>
          </Space>
        </>
      </Card>
      )}
    </Space>
  );
};

export default UserCredits;